import React from 'react';
import { CheckCircle2 } from 'lucide-react';

const features = [
    'Reduce operational costs by up to 60%',
    'Zero manual data entry errors',
    '24/7 system uptime & monitoring',
    'Seamless integration with 5000+ apps',
    'Custom dashboards & real-time reporting',
    'Scalable architecture that grows with you',
];

const Features: React.FC = () => {
    return (
        <section className="py-24 bg-navy-800 relative overflow-hidden">
            {/* Decorative gradient */}
            <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-white/[0.1] to-transparent" />

            <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center">
                <div>
                    <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                        Why Leading Businesses Choose AutomateIt.ng
                    </h2>
                    <p className="text-text-muted text-lg mb-8 leading-relaxed">
                        We combine technical expertise with business acumen to build systems that actually move the needle. No bloat, no unnecessary complexity—just pure efficiency.
                    </p>

                    <div className="grid sm:grid-cols-2 gap-4">
                        {features.map((feature, index) => (
                            <div key={index} className="flex items-start gap-3">
                                <CheckCircle2 className="w-6 h-6 text-brand-cyan shrink-0" />
                                <span className="text-text-muted">{feature}</span>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="relative">
                    <div className="absolute inset-0 bg-gradient-to-r from-brand-cyan to-brand-blue rounded-2xl blur-2xl opacity-20" />
                    <div className="relative bg-navy-900 border border-white/[0.1] rounded-2xl p-8 shadow-2xl">
                        <div className="flex items-center justify-between mb-8 border-b border-white/[0.05] pb-4">
                            <div className="flex gap-2">
                                <div className="w-3 h-3 rounded-full bg-red-500" />
                                <div className="w-3 h-3 rounded-full bg-yellow-500" />
                                <div className="w-3 h-3 rounded-full bg-green-500" />
                            </div>
                            <div className="text-xs text-text-muted font-mono">n8n workflow active</div>
                        </div>
                        {/* Abstract representation of a workflow */}
                        <div className="space-y-4">
                            <div className="flex items-center gap-4">
                                <div className="w-10 h-10 rounded-lg bg-green-600/20 flex items-center justify-center text-green-500">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-6 h-6"><path d="M3 21l18 0M5 21l14 0M3 10h18M5 6l14 0" /></svg>
                                </div>
                                <div className="h-px flex-1 bg-white/[0.1]" />
                                <div className="px-3 py-1 rounded bg-white/[0.05] text-xs text-text-muted">New Lead</div>
                            </div>

                            <div className="flex justify-center">
                                <div className="w-px h-8 bg-white/[0.1]" />
                            </div>

                            <div className="flex items-center gap-4">
                                <div className="w-10 h-10 rounded-lg bg-blue-600/20 flex items-center justify-center text-blue-500">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-6 h-6"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
                                </div>
                                <div className="h-px flex-1 bg-white/[0.1]" />
                                <div className="px-3 py-1 rounded bg-white/[0.05] text-xs text-text-muted">AI Analysis</div>
                            </div>

                            <div className="flex justify-center">
                                <div className="w-px h-8 bg-white/[0.1]" />
                            </div>

                            <div className="flex items-center gap-4">
                                <div className="w-10 h-10 rounded-lg bg-orange-600/20 flex items-center justify-center text-orange-500">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-6 h-6"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" /></svg>
                                </div>
                                <div className="h-px flex-1 bg-white/[0.1]" />
                                <div className="px-3 py-1 rounded bg-white/[0.05] text-xs text-text-muted">Auto-Reply</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Features;
